import type { QueryClient } from "@tanstack/react-query";
import { clinicApi } from "@/domains/multi/clinicApi";
import { CLINIC_HOME_PATH, CLINIC_SUBSCRIPTION_PATH } from "@/domains/multi/clinicMemberPaths";
import type { PlanChangeSecretaryCandidates } from "@/domains/subscription/planChangeSecretaryCandidates";
import {
    isClinicKeepPlanDowngrade,
    isUpgradePlan,
    normalizePlanSlug,
    SubscriptionPlanIds,
} from "@/domains/subscription/plans";
import { hasSoloSecretaryLinks } from "@/components/subscription/useSoloSecretaryMigration";

export function soloSecretaryPlanChangeQueryKey(userId: string | undefined) {
    return ["billing", "change-plan", "secretary-candidates", userId] as const;
}

export function clinicSecretaryPlanChangeQueryKey(userId: string | undefined) {
    return ["clinic", "change-plan", "secretary-candidates", userId] as const;
}

export async function fetchSoloSecretaryMigrationSummary(
    queryClient: QueryClient,
    userId: string,
    _useClinicBilling = true,
): Promise<PlanChangeSecretaryCandidates> {
    return queryClient.fetchQuery({
        queryKey: clinicSecretaryPlanChangeQueryKey(userId),
        queryFn: async () => {
            const { data } = await clinicApi.fetchChangePlanSecretaryCandidates();
            return data;
        },
    });
}

export type ClinicUpgradeStep = "secretaryMigration" | "confirm" | "method";

export type PlanChangeRoutingOptions = {
    hasActivePaidSubscription: boolean;
    currentPlanId: string;
    targetPlanId: string;
};

function shouldCheckoutUpgradeWithoutBilling(options: PlanChangeRoutingOptions): boolean {
    return !options.hasActivePaidSubscription && isUpgradePlan(options.currentPlanId, options.targetPlanId);
}

export function resolveStepAfterClinicReady(
    summary: PlanChangeSecretaryCandidates | undefined,
    options?: PlanChangeRoutingOptions,
): ClinicUpgradeStep {
    if (hasSoloSecretaryLinks(summary)) {
        return "secretaryMigration";
    }

    if (options && shouldCheckoutUpgradeWithoutBilling(options)) {
        return "method";
    }

    return "confirm";
}

export function resolveStepAfterSecretaryMigration(options: PlanChangeRoutingOptions): "confirm" | "method" {
    return shouldCheckoutUpgradeWithoutBilling(options) ? "method" : "confirm";
}

export function resolveSecretaryMigrationBackStep(clinicSetupCompleted: boolean): "confirm" | "plan" {
    return clinicSetupCompleted ? "confirm" : "plan";
}

/**
 * After refreshMe, Multi→Básico/Pro lands on clinic Assinatura (BR-MDR-40–43, AC-MDR-05).
 * Never returns a profissional-only path for clinic-keep downgrades (AC-MDR-06).
 */
export function resolvePostPlanChangeLanding(args: {
    previousPlanId: string | null | undefined;
    targetPlanId: string | null | undefined;
}): string | null {
    if (isClinicKeepPlanDowngrade(args.previousPlanId, args.targetPlanId)) {
        return CLINIC_SUBSCRIPTION_PATH;
    }

    if (normalizePlanSlug(args.targetPlanId) === SubscriptionPlanIds.clinic) {
        return CLINIC_HOME_PATH;
    }

    return null;
}
